/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/pages/auth/AuthPage`, `src/pages/home/HomePage`).
 */
import React from "react";
import { Redirect, Route, Switch, withRouter, Prompt, useLocation } from "react-router-dom";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useLastLocation } from "react-router-last-location";
import HomePage from "../pages/home/HomePage";
import ErrorsPage from "../pages/errors/ErrorsPage";
import LogoutPage from "../modules/auth/Logout";
import { LayoutContextProvider } from "../../_metronic";
import Layout from "../../_metronic/layout/Layout";
import * as routerHelpers from "../router/RouterHelpers";
import AuthPage from "../modules/auth/AuthPage";
import ModalManager from "../modules/_modal/pages/ModalManager";
import TabManager from "../modules/_tab/pages/TabManager";
import DoorKnock from "../modules/prospect/partials/prospectMobile/ProspectMobile";
import MarketingBoards from "../modules/marketing/pages/MarketingBoards";
import PasswordExpiryModal from "../modules/auth/components/PasswordExpiryModal";
import * as auth from "../modules/auth/_redux/auth.duck";

export const Routes = withRouter(({ history }) => {
  const lastLocation = useLastLocation();
  const location = useLocation();
  const dispatch = useDispatch();
  routerHelpers.saveLastLocation(lastLocation);
  const { isAuthorized, menuConfig, userLastLocation, userId, passwordModal, user } = useSelector(
    ({ auth, urls, builder: { menuConfig } }) => ({
      menuConfig,
      isAuthorized: auth.user != null,
      userLastLocation: routerHelpers.getLastLocation(),
      userId: auth?.user?.id,
      passwordModal: auth.passwordModal,
      user: auth.user,
    }),
    shallowEqual
  );
  
  // Always check password expiry when user becomes authorized
  React.useEffect(() => {
    if (isAuthorized && user?.id) {
      // Always check password expiry to get the latest status
      dispatch(auth.actions.checkPasswordExpiry());
    }
  }, [isAuthorized, user?.id, dispatch]);

  const handleClosePasswordModal = () => {
    // Store dismiss flag in sessionStorage for notify mode
    if (passwordModal?.mode === 'notify' && user?.id) {
      sessionStorage.setItem(`password-notification-dismissed-${user.id}`, 'true');
    }
    dispatch(auth.actions.hidePasswordModal());
  };

  const handlePasswordChanged = () => {
    // Clear session flags when password is successfully changed
    if (user?.id) {
      sessionStorage.removeItem(`password-notification-dismissed-${user.id}`);
      sessionStorage.removeItem(`password-required-${user.id}`);
      
      // Re-check password status after change
      dispatch(auth.actions.checkPasswordExpiry());
    }
    dispatch(auth.actions.hidePasswordModal());
  };

  const handleSwitchToForce = () => {
    // Switch from notify mode to force mode
    dispatch(auth.actions.showPasswordModal('force'));
  };

  return (
    /* Create `LayoutContext` from current `history` and `menuConfig`. */
    <LayoutContextProvider history={history} menuConfig={menuConfig}>
      <ModalManager />
      <TabManager />
      {/* Block route transitions while password change is required */}
      <Prompt when={Boolean(passwordModal?.show)} message={() => false} />

      <Switch>
        {!isAuthorized ? (
          /* Render auth page when user at `/auth` and not authorized. */
          <AuthPage />
        ) : (
          /* Otherwise redirect to root page (`/`) */
          <Redirect from="/auth" to={userLastLocation} />
        )}

        <Route path="/error" component={ErrorsPage} />
        <Route path="/logout" component={LogoutPage} />
        {isAuthorized && <Route path="/door_knock" component={DoorKnock} />}
        {!isAuthorized ? (
          /* Redirect to `/auth` when user is not authorized */
          <Redirect to="/auth/login" />
        ) : (
          <Layout>
            <HomePage userLastLocation={userLastLocation} />
          </Layout>
        )}
      </Switch>
      
      <PasswordExpiryModal
        mode={passwordModal?.mode}
        show={passwordModal?.show}
        onClose={handleClosePasswordModal}
        onPasswordChanged={handlePasswordChanged}
        onSwitchToForce={handleSwitchToForce}
      />
    </LayoutContextProvider>
  );
});
